<template>
  <div class="columns -spacing-static-d is-tablet">
    <!-- Left column: coverage form, driven entirely by stateCoverageConfigs -->
    <div class="column is-5-desktop">
      <div class="quote-header">
        <legend>{{ name }}</legend>
      </div>

      <template v-for="(entry, entryIndex) in stateConfig.fields">
        <!-- ── Novel blocks ─────────────────────────────────────────────── -->
        <template v-if="entry.novelBlock">
          <!-- Policy Effective Date -->
          <div
            v-if="entry.novelBlock === 'effective-date'"
            :key="'effective-date-' + entryIndex"
            class="has-placeholders effective-date-field"
          >
            <div class="limit-container" v-if="coverageStepIndex === 0">
              <label class="input -spacing-static-c">
                <masked-input
                  :class="{
                    input__field: true,
                    error: errors.has('policyEffectiveDate')
                  }"
                  type="text"
                  name="policyEffectiveDate"
                  placeholder="Policy Effective Date"
                  :keepCharPositions="true"
                  v-model="form.policyEffectiveDate"
                  v-validate="'required|effectiveDateValidator|isLeapYear'"
                  :guide="true"
                  :placeholderChar="'_'"
                  :pipe="autoCorrectedDatePipe"
                  :mask="[
                    /\d/,
                    /\d/,
                    '/',
                    /\d/,
                    /\d/,
                    '/',
                    /\d/,
                    /\d/,
                    /\d/,
                    /\d/
                  ]"
                  @blur="
                    dateInputChanged(
                      $event,
                      'policyEffectiveDate',
                      form.policyEffectiveDate
                    )
                  "
                ></masked-input>
                <span class="input__label">Policy Effective Date</span>
              </label>
              <b-icon pack="fas" icon="calendar" class="calendar-icon"></b-icon>
              <b-datepicker
                name="policyEffectiveDate1"
                @input="changedPolicyDate"
                v-model="selectedDate"
                :min-date="validationOptions.effectiveDateMin"
                :max-date="validationOptions.effectiveDateMax"
                :mobile-native="false"
              ></b-datepicker>
            </div>
            <label class="input -spacing-static-c" v-else>
              <input
                class="input__field"
                name="policyEffectiveDate"
                type="text"
                :value="getDateValue"
                disabled
              />
              <span class="input__label">Policy Effective Date</span>
            </label>
          </div>

          <!-- GA: Uninsured Motorist Coverage Type radios -->
          <div
            v-else-if="entry.novelBlock === 'ga-um-type'"
            :key="'ga-um-type-' + entryIndex"
            v-show="!entry.showWhen || entry.showWhen(form, coverageStepIndex)"
            class="-spacing-static-c"
          >
            <label
              :class="{
                error: errors.has('uninsuredCoverageType')
              }"
            >
              Uninsured Motorist Coverage Type
              <i class="icon icon--info tool-tip">
                <div class="content">
                  <p v-html="uninsuredCoverageTypeTooltip"></p>
                  <i></i>
                </div>
              </i>
            </label>
            <div>
              <label
                class="input -spacing-static-c"
                :class="{
                  radio: true,
                  error: errors.has('reduced')
                }"
              >
                <input
                  class="radio__field"
                  type="radio"
                  name="YMM"
                  :value="radios.toggleForCoverageType[0].value"
                  :disabled="coverageStepIndex !== 0"
                  v-model="form.uninsuredCoverageType"
                  @change="sendUpdate('uninsuredCoverageType')"
                />
                <span class="radio__icon icon icon--radio"></span>
                <span class="radio__label">
                  {{ radios.toggleForCoverageType[0].key }}
                  <br />
                </span>
              </label>
              <label
                class="input -spacing-static-c"
                :class="{
                  radio: true,
                  error: errors.has('added')
                }"
              >
                <input
                  class="radio__field"
                  type="radio"
                  name="YMM"
                  :value="radios.toggleForCoverageType[1].value"
                  :disabled="coverageStepIndex !== 0"
                  v-model="form.uninsuredCoverageType"
                  @change="sendUpdate('uninsuredCoverageType')"
                />
                <span class="radio__icon icon icon--radio"></span>
                <span class="radio__label">
                  {{ radios.toggleForCoverageType[1].key }}
                  <br />
                </span>
              </label>
            </div>
          </div>

          <!-- FL: Uninsured/Underinsured Motorist Stacking -->
          <div
            v-else-if="
              entry.novelBlock === 'fl-um-stacking' &&
                (!entry.showWhen || entry.showWhen(form, coverageStepIndex))
            "
            :key="'fl-um-stacking-' + entryIndex"
            class="-spacing-static-e"
          >
            <label
              :for="
                coverageStepIndex === 0
                  ? 'uninsuredCoverageType'
                  : 'uninsuredCoverageTypeValue'
              "
              :class="{
                error: errors.has('uninsuredCoverageType')
              }"
              >Uninsured/Underinsured Motorist Stacking</label
            >
            <div class="select-wrapper" v-show="coverageStepIndex === 0">
              <v-select
                v-model="form.uninsuredCoverageType"
                :searchable="false"
                :options="uninsuredMotoristStackingOptions"
                id="uninsuredCoverageType"
                v-on:input="sendUpdate('uninsuredCoverageType')"
                v-validate="'required'"
                name="uninsuredCoverageType"
                :class="{
                  error: errors.has('uninsuredCoverageType'),
                  dropdown_toggle_disabled_bg: true
                }"
              ></v-select>
            </div>
            <div class="input" v-show="coverageStepIndex !== 0">
              <input
                class="input__field"
                name="uninsuredCoverageType"
                id="uninsuredCoverageTypeValue"
                type="text"
                v-model="form.uninsuredCoverageType"
                disabled
              />
            </div>
          </div>

          <!-- FL: PIP Deductible + PIP Wage Loss Option -->
          <div
            v-else-if="entry.novelBlock === 'fl-pip'"
            :key="'fl-pip-' + entryIndex"
          >
            <div class="-spacing-static-c">
              <label
                for="pipDeductible"
                :class="{
                  error: errors.has('pipDeductible')
                }"
                >Personal Injury Protection Deductible</label
              >
              <div class="select-wrapper">
                <v-select
                  v-model="form.pipDeductible"
                  :searchable="false"
                  :options="selectsCoverage[selectedState].pipDeductible"
                  id="pipDeductible"
                  v-on:input="sendUpdate('pipDeductible')"
                  v-validate="'required'"
                  name="pipDeductible"
                  :class="{
                    error: errors.has('pipDeductible')
                  }"
                ></v-select>
              </div>
            </div>
            <div class="-spacing-static-c">
              <label
                for="pipDeductibleType"
                :class="{
                  error: errors.has('pipDeductibleType')
                }"
                >PIP Wage Loss Option</label
              >
              <div class="select-wrapper">
                <v-select
                  v-model="form.pipDeductibleType"
                  :searchable="false"
                  :options="selectsCoverage[selectedState].pipDeductibleType"
                  id="pipDeductibleType"
                  v-on:input="sendUpdate('pipDeductibleType')"
                  v-validate="'required'"
                  name="pipDeductibleType"
                  :class="{
                    error: errors.has('pipDeductibleType')
                  }"
                ></v-select>
              </div>
            </div>
          </div>
        </template>

        <!-- ── Standard fields ──────────────────────────────────────────── -->
        <template
          v-else-if="!entry.showWhen || entry.showWhen(form, coverageStepIndex)"
        >
          <!-- Package note, sits above the first packaged field -->
          <p
            v-if="entry.packageMessage && form.bodilyInjury"
            :key="entry.key + 'PackageMessage'"
          >
            We have created the below package based on your selections above. If you would like to make further adjustments, please call
            Customer Concierge at
            <a
              style="text-decoration:underline;"
              :href="'tel:1-' + $phoneNumber"
              >{{ $phoneNumber }}</a
            >.
          </p>

          <div :key="entry.key" :class="entry.spacing || '-spacing-static-c'">
            <label
              :for="fieldLabelFor(entry)"
              :class="fieldLabelClass(entry)"
              >{{ entry.label }}</label
            >
            <i v-if="entry.tooltip" class="icon icon--info tool-tip">
              <div class="content">
                <p>{{ entry.tooltip }}</p>
                <i></i>
              </div>
            </i>

            <!-- Packaged: value is set programmatically, always disabled -->
            <template v-if="isPackaged(entry)">
              <div class="input">
                <input
                  class="input__field"
                  :name="entry.key"
                  :id="entry.key"
                  type="text"
                  :value="packagedValue(entry)"
                  disabled
                />
              </div>
            </template>

            <!-- Vehicle-specific: always-editable array select -->
            <template v-else-if="entry.type === 'select-array'">
              <!-- FL collision: comprehensive declined locks this out -->
              <template v-if="entry.renderCustom === 'coll-comp-declined'">
                <div
                  v-show="
                    form.comprehensiveDeductible[coverageStepIndex] !==
                      'I decline this coverage'
                  "
                  class="select-wrapper"
                >
                  <v-select
                    v-model="form[entry.key][coverageStepIndex]"
                    :searchable="false"
                    :options="getFieldOptions(entry)"
                    :id="entry.key"
                    v-on:input="sendUpdate(entry.key)"
                    v-validate="'required'"
                    :name="entry.key"
                    :class="fieldSelectClass(entry)"
                  ></v-select>
                </div>
                <div
                  class="input"
                  v-show="
                    form.comprehensiveDeductible[coverageStepIndex] ===
                      'I decline this coverage'
                  "
                >
                  <input
                    class="input__field"
                    :name="entry.key"
                    :id="entry.key + 'Decline'"
                    type="text"
                    value="Comprehensive Coverage Declined"
                    disabled
                  />
                </div>
              </template>

              <!-- FL rental: either deductible declined locks this out -->
              <template
                v-else-if="entry.renderCustom === 'rental-declined-msg'"
              >
                <div v-show="!eitherDeductibleDeclined" class="select-wrapper">
                  <v-select
                    v-model="form[entry.key][coverageStepIndex]"
                    :searchable="false"
                    :options="getFieldOptions(entry)"
                    :id="entry.key"
                    v-on:input="sendUpdate(entry.key)"
                    v-validate="'required'"
                    :name="entry.key"
                    :class="fieldSelectClass(entry)"
                  ></v-select>
                </div>
                <div class="input" v-show="eitherDeductibleDeclined">
                  <input
                    class="input__field"
                    :name="entry.key"
                    :id="entry.key + 'Decline'"
                    type="text"
                    value="Coverage Declined"
                    disabled
                  />
                </div>
              </template>

              <div v-else class="select-wrapper">
                <v-select
                  v-model="form[entry.key][coverageStepIndex]"
                  :searchable="false"
                  :options="getFieldOptions(entry)"
                  :id="entry.key"
                  v-on:input="sendUpdate(entry.key)"
                  v-validate="'required'"
                  :name="entry.key"
                  :class="fieldSelectClass(entry)"
                ></v-select>
              </div>
            </template>

            <!-- Policy-level, always editable -->
            <div
              v-else-if="entry.type === 'select-open'"
              class="select-wrapper"
            >
              <v-select
                v-model="form[entry.key]"
                :searchable="false"
                :options="getFieldOptions(entry)"
                :id="entry.key"
                v-on:input="sendUpdate(entry.key)"
                v-validate="'required'"
                :name="entry.key"
                :class="fieldSelectClass(entry)"
              ></v-select>
            </div>

            <!-- Policy-level: editable on vehicle 0, locked on vehicle 1+ -->
            <template v-else-if="entry.type === 'select'">
              <div class="select-wrapper" v-show="coverageStepIndex === 0">
                <v-select
                  v-model="form[entry.key]"
                  :searchable="false"
                  :options="getFieldOptions(entry)"
                  :id="entry.key"
                  v-on:input="sendUpdate(entry.key)"
                  v-validate="'required'"
                  :name="entry.key"
                  :class="{
                    error: errors.has(entry.key),
                    dropdown_toggle_disabled_bg: true
                  }"
                ></v-select>
              </div>
              <div class="input" v-show="coverageStepIndex !== 0">
                <input
                  class="input__field"
                  :name="entry.key"
                  :id="entry.key + 'Value'"
                  type="text"
                  v-model="form[entry.key]"
                  disabled
                />
              </div>
            </template>
          </div>
        </template>
      </template>
    </div>

    <!-- Right column: quote summary -->
    <div
      class="column is-offset-1-desktop is-6-desktop"
      style="position: relative"
    >
      <div class="quote-header">
        <legend class="quote-legend">Your Quote</legend>
      </div>
      <div class="box box--bordered -spacing-static-c">
        <div
          v-if="quoteForm.vehicles.length > 1 && calculated"
          @click="$emit('previousVehicleClick')"
          :class="{
            'multi-vehicle-chevron__left': true,
            active: quoteForm.vehicles.length > 1 && coverageStepIndex !== 0,
            inactive: quoteForm.vehicles.length > 1 && coverageStepIndex === 0
          }"
        >
          <i class="fas fa-2x fa-chevron-left"></i>
        </div>
        <div
          v-if="quoteForm.vehicles.length > 1 && calculated"
          @click="$emit('nextVehicleClick')"
          :class="{
            'multi-vehicle-chevron__right': true,
            active: quoteForm.vehicles.length > 1 && coverageStepIndex === 0,
            inactive: quoteForm.vehicles.length > 1 && coverageStepIndex !== 0
          }"
        >
          <i class="fas fa-2x fa-chevron-right"></i>
        </div>
        <div>
          <h3
            v-if="quoteForm.vehicles.length > 1"
            class="heading right-section-first-container right-section-first-container--wpt"
          >
            Vehicle {{ coverageStepIndex + 1 }} of
            {{ quoteForm.vehicles.length }}
            <p
              class="is-paddingless is-marginless"
              style="font-weight: 400;font-family: PorscheNextRegular, 'Arial Narrow', Arial, sans-serif;"
            >
              (use left/right arrows to change vehicles)
            </p>
          </h3>
          <h3
            v-else
            class="heading right-section-first-container right-section-first-container--wpt"
          >
            Vehicle
          </h3>
          <div class="columns is-mobile">
            <div class="column is-4">Model:</div>
            <div class="column is-8">
              {{ currentVehicle }}
            </div>
          </div>
          <div
            v-if="
              quoteForm.vehicles[coverageStepIndex] &&
                quoteForm.vehicles[coverageStepIndex].vin &&
                !isThisSquish(quoteForm.vehicles[coverageStepIndex].vin)
            "
            class="columns is-mobile"
          >
            <div class="column is-4">VIN:</div>
            <div class="column is-8">
              {{ quoteForm.vehicles[coverageStepIndex].vin }}
            </div>
          </div>
        </div>
      </div>

      <!-- States with an active Unlimited implementation get the plan selector -->
      <CoveragePricing
        v-if="stateConfig.unlimited && calculated"
        :estimatedMonthlyPayment="estimatedMonthlyPayment"
        :isUnlimited="isUnlimited"
        :vehicleIndex="coverageStepIndex"
        :quoteState="selectedState"
        :singlePaymentOption="singlePaymentOption"
        :monthlyPayDownPayment="monthlyPayDownPayment"
        :feeTooltip="feeTooltip"
        @toggleUnlimited="$emit('toggleUnlimited', coverageStepIndex)"
      />
      <div v-else class="box">
        <div class="columns is-mobile has-margin-top-1">
          <div class="column is-7">Base Charge:</div>
          <div class="column is-5">
            <span v-if="quoteResponse">${{ roundedBaseRate.toFixed(2) }}</span>
            <span v-else>-</span>&nbsp;per month
          </div>
        </div>

        <div class="columns is-mobile">
          <div class="column is-7">Per Mile Charge:</div>
          <div class="column is-5">
            <span v-if="quoteResponse">
              ${{
                quoteResponse.policies[0].vehicleInfo[
                  coverageStepIndex
                ].premiumInfo.perMileRate.toFixed(3)
              }}
            </span>
            <span v-else>-</span>&nbsp;per mile
          </div>
        </div>

        <div class="columns is-mobile">
          <div class="column is-7">Estimated monthly cost:</div>
          <div style="position: relative" class="column is-5">
            <span style="bottom: 0; position: absolute" v-if="quoteResponse">
              ${{
                isFloat(getMonthlyCostForIndividualVehicle(coverageStepIndex))
                  ? getMonthlyCostForIndividualVehicle(
                      coverageStepIndex
                    ).toFixed(2)
                  : getMonthlyCostForIndividualVehicle(coverageStepIndex)
              }}
            </span>
            <span v-else>-</span>
          </div>
        </div>

        <div class="columns is-mobile">
          <div class="column is-12" v-if="quoteResponse">
            (Based on your estimated driving of
            <span>
              {{
                quoteResponse.policies[0].vehicleInfo[coverageStepIndex]
                  .premiumInfo.estMonthlyMiles
              }}
            </span>
            miles per month)
          </div>
        </div>
        <div>
          <h3 class="heading">
            Total Policy Premium
          </h3>
          <div class="columns is-mobile">
            <div class="column is-7">
              Estimated Monthly Payment (includes fees):
              <i class="icon icon--info tool-tip">
                <div class="content">
                  <p>{{ feeTooltip }}</p>
                  <i></i>
                </div>
              </i>
            </div>
            <div
              v-if="quoteResponse"
              class="column is-5"
              style="position:relative;"
            >
              <span style="position:absolute; bottom:0;">
                ${{ estimatedMonthlyPayment }}
              </span>
            </div>
            <div v-else class="column is-4">-</div>
          </div>
        </div>
        <div>
          <h3 class="heading">
            Down Payment
          </h3>
          <div class="columns is-mobile">
            <div class="column is-7">
              Due Today:
            </div>
            <div v-if="monthlyPayDownPayment" class="column is-5">
              ${{ monthlyPayDownPayment }}
              <i class="icon icon--info tool-tip">
                <div class="content">
                  <p>{{ feeTooltip }}</p>
                  <i></i>
                </div>
              </i>
            </div>
            <div v-else class="column is-4">-</div>
          </div>
        </div>
      </div>
      <div>
        <p v-if="calculated">
          For your convenience, this quote has been sent to the email address
          you provided. You can access it anytime by clicking the link in the
          email, or via the "Retrieve your quote" window on the homepage.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable prettier/prettier */
import { mapGetters } from 'vuex';
import moment from 'moment';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import * as _ from 'lodash';
import { setTimeout } from 'timers';
import CoveragePricing from '@/components/shared/CoveragePricing.vue';
import { monthlyFees, monthlyFeeTooltip } from '@/utils/fees.js';
import { STATE_COVERAGE_CONFIGS } from './stateCoverageConfigs';

const DECLINED = 'I decline this coverage';

export default {
  name: 'StateCoveragesForm',
  components: {
    CoveragePricing
  },
  props: {
    calculated: {
      type: Boolean,
      required: true
    },
    coverageStepIndex: {
      type: Number,
      required: true
    },
    validationOptions: {
      type: Object,
      required: true
    },
    selectedState: {
      type: String,
      required: true
    },
    coverageForm: {
      type: Object,
      required: true
    },
    isUnlimited: {
      type: Boolean
    },
    singlePayPayment: {
      type: String
    },
    monthlyPayDownPayment: {
      type: String
    },
    // GA's Uninsured Motorist Coverage Type radio labels/values.
    radios: {
      type: Object,
      default: () => ({
        toggleForCoverageType: [
          { key: 'Reduced By', value: 'R' },
          { key: 'Added On', value: 'A' }
        ]
      })
    },
    uninsuredCoverageTypeTooltip: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    name: 'Your Coverage',
    form: {
      bodilyInjury: null,
      propertyDamage: null,
      comprehensiveDeductible: [],
      collisionDeductible: [],
      propertyDamageDeductible: [],
      uninsuredBodilyInjury: null,
      underinsuredMotorist: null,
      uninsuredPropertyDamage: null,
      uninsuredCoverageType: null,
      personalInjuryProtection: null,
      medicalPayments: null,
      pipDeductible: null,
      pipDeductibleType: null,
      rentalReimbursement: [],
      policyEffectiveDate: moment().format('MM/DD/YYYY')
    },
    selectedDate: null,
    oldDate: moment().format('MM/DD/YYYY')
  }),
  mounted() {
    if (!_.isEmpty(this.quoteForm.coverage)) {
      this.$validator
        .validate('policyEffectiveDate', this.form.policyEffectiveDate)
        .then(isValid => {
          if (!isValid) {
            setTimeout(() => {
              this.$el
                .querySelector('[name="policyEffectiveDate"]')
                .scrollIntoView();
            }, 100);
          }
        });

      if (
        !Array.isArray(this.quoteForm.vehicles) ||
        this.quoteForm.vehicles.length === 0
      ) {
        throw new Error(`Trying to select coverage with zero vehicles`);
      }

      const vehicle = this.quoteForm.vehicles[this.coverageStepIndex];
      if (vehicle && vehicle.leaseOwnFinance.value === 'owned') {
        const stateSelects = this.selectsCoverage[this.selectedState];
        if (!stateSelects.comprehensiveDeductible.includes(DECLINED)) {
          stateSelects.comprehensiveDeductible.push(DECLINED);
        }
        if (!stateSelects.collisionDeductible.includes(DECLINED)) {
          stateSelects.collisionDeductible.push(DECLINED);
        }
      }
    }

    if (this.stateConfig.onMount) {
      this.stateConfig.onMount(this);
    }
  },
  computed: {
    ...mapGetters([
      'getMonthlyCostForIndividualVehicle',
      'isThisSquish',
      'isFloat',
      'roundAmountTo',
      'quoteResponse',
      'quoteForm',
      'selectsAgencyCodes',
      'selectsCoverage',
      'selectsDrivers'
    ]),
    stateConfig() {
      return STATE_COVERAGE_CONFIGS[this.selectedState] || {};
    },
    // Fees are policy-level: the Mileage Monitoring fee only drops off once
    // EVERY vehicle is on the Unlimited plan. `isUnlimited` is the plan for the
    // vehicle currently on screen, so it is the wrong flag for fee maths.
    allVehiclesUnlimited() {
      const vehicles = (this.quoteForm && this.quoteForm.vehicles) || [];
      return (
        vehicles.length > 0 && vehicles.every(vehicle => vehicle.unlimitedVehicle)
      );
    },
    feeTooltip() {
      return monthlyFeeTooltip(this.selectedState, this.allVehiclesUnlimited);
    },
    findBilling() {
      const policy =
        this.quoteResponse &&
        this.quoteResponse.policies &&
        this.quoteResponse.policies[0];
      return (policy && policy.findBilling) || null;
    },
    // The rated figure from findBilling wins when the API returns one; monthly
    // cost plus the state's monthly fees is the fallback. QuoteQuote and
    // QuotePayment apply the same precedence so all three agree.
    estimatedMonthlyPayment() {
      const rated =
        this.findBilling &&
        Number.parseFloat(this.findBilling.estimatedMonthlyPayment);
      if (Number.isFinite(rated)) {
        return rated.toFixed(2).toString();
      }
      return (
        this.roundAmountTo(this.getTotalMonthlyCost || 0, 2) +
        this.getTotalMonthlyFees()
      )
        .toFixed(2)
        .toString();
    },
    // The paid-in-full chain in src/utils/downPayment.js already accounts for
    // fees at every level, so nothing is added here.
    singlePaymentOption() {
      return this.singlePayPayment || '';
    },
    eitherDeductibleDeclined() {
      return (
        this.form.comprehensiveDeductible[this.coverageStepIndex] ===
          DECLINED ||
        this.form.collisionDeductible[this.coverageStepIndex] === DECLINED
      );
    },
    currentVehicle() {
      const vehicle =
        this.quoteForm &&
        this.quoteForm.vehicles &&
        this.quoteForm.vehicles[this.coverageStepIndex];
      if (!vehicle || !vehicle.year) {
        return null;
      }
      const vehicleYear = vehicle.year || '';
      const vehicleMake = vehicle.make || '';
      const vehicleModel = vehicle.model || '';
      const vehicleStyle = vehicle.bodyStyle || '';
      return `${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleStyle}`;
    },
    autoCorrectedDatePipe() {
      return createAutoCorrectedDatePipe('mm/dd/yyyy');
    },
    coverageFormWatch() {
      return _.cloneDeep(this.coverageForm);
    },
    getDateValue() {
      if (this.form.policyEffectiveDate) {
        return this.form.policyEffectiveDate;
      }
    },
    getTotalMonthlyCost() {
      if (
        this.quoteResponse &&
        this.quoteResponse.policies[0] &&
        this.quoteResponse.policies[0].vehicleInfo
      ) {
        let totalMonthlyCost = 0;
        const that = this;
        this.quoteResponse.policies[0].vehicleInfo.forEach(function(
          vehicle,
          index
        ) {
          totalMonthlyCost += that.getMonthlyCostForIndividualVehicle(index);
        });
        return totalMonthlyCost;
      }
    },
    roundedBaseRate() {
      let baseRateFromResponse = 0.0;
      if (this.quoteResponse) {
        baseRateFromResponse = this.quoteResponse.policies[0].vehicleInfo[
          this.coverageStepIndex
        ].premiumInfo.baseRate.toFixed(2);
      }
      return this.roundAmountTo(baseRateFromResponse, 2);
    },
    // Uninsured BI options are capped to the Bodily Injury tier selected, with
    // 'I decline this coverage' appended. WI is the exception: it has no
    // decline option.
    uninsuredBodilyInjuryOptions() {
      const selectedIndex = this.selectsCoverage[
        this.selectedState
      ].bodilyInjury.indexOf(this.form.bodilyInjury);
      let options = _.cloneDeep(
        this.selectsCoverage[this.selectedState].uninsuredBodilyInjury
      );
      if (selectedIndex !== null) {
        options = options.slice(0, selectedIndex + 1);
      }
      if (this.selectedState === 'WI') {
        return options;
      }
      return this.withDeclineOption(options);
    },
    underinsuredMotoristOptions() {
      const selectedIndex = this.selectsCoverage[
        this.selectedState
      ].bodilyInjury.indexOf(this.form.bodilyInjury);
      let options = _.cloneDeep(
        this.selectsCoverage[this.selectedState].underinsuredMotorist || []
      );
      if (selectedIndex !== null) {
        options = options.slice(0, selectedIndex + 1);
      }
      return this.withDeclineOption(options);
    },
    // FL: Property Damage tiers are gated by the Bodily Injury tier selected.
    propertyDamageOptions() {
      const stored = this.selectsCoverage[this.selectedState].propertyDamage;
      switch (this.form.bodilyInjury) {
        case null:
        case '$10,000/$20,000':
          return stored.slice(0, 1);
        case '$25,000/$50,000':
          return [stored[0], stored[2], stored[3]];
        case '$50,000/$100,000':
          return [stored[2], stored[3], stored[4]];
        case '$100,000/$300,000':
          return [stored[3], stored[4]];
        case '$300,000/$300,000':
          return [stored[4]];
        case '$250,000/$500,000':
        case '$500,000/$500,000':
          return [stored[4], stored[5]];
        default:
          return [];
      }
    },
    uninsuredMotoristStackingOptions() {
      return ['Stacked', 'Unstacked'];
    },
    // TN / WI (and PA, once migrated) cap deductibles at the vehicle's stated
    // value.
    compSelects() {
      return this.statedValueCappedOptions('comprehensiveDeductible');
    },
    collSelects() {
      return this.statedValueCappedOptions('collisionDeductible');
    }
  },
  methods: {
    withDeclineOption(options) {
      const last = options[options.length - 1];
      if (last && last.toLowerCase() === DECLINED.toLowerCase()) {
        return options;
      }
      options.push(DECLINED);
      return options;
    },
    statedValueCappedOptions(field) {
      const vehicle = this.quoteForm.vehicles[this.coverageStepIndex];
      const stored = this.selectsCoverage[this.selectedState][field];
      if (!vehicle || !vehicle.statedValue) {
        return this.withDeclineOption(_.cloneDeep(stored));
      }
      const maxValue = Number.parseInt(
        vehicle.statedValue
          .toString()
          .replace(/\$/g, '')
          .replace(/,/g, '')
      );
      const options = stored.filter(
        opt =>
          Number.parseInt(
            opt
              .replace(/\$/g, '')
              .replace(/,/g, '')
          ) <= maxValue
      );
      return this.withDeclineOption(options);
    },
    isPackaged(field) {
      return !!(
        this.stateConfig.packageCoverages &&
        this.stateConfig.packageCoverages.includes(field.key)
      );
    },
    packagedValue(field) {
      const value = this.form[field.key];
      return Array.isArray(value) ? value[this.coverageStepIndex] : value;
    },
    getFieldOptions(field) {
      if (field.optionsComputed) {
        return this[field.optionsKey] || [];
      }
      const stateSelects = this.selectsCoverage[this.selectedState] || {};
      return stateSelects[field.optionsKey || field.key] || [];
    },
    fieldLabelFor(field) {
      if (field.type === 'select' && !this.isPackaged(field)) {
        return this.coverageStepIndex === 0 ? field.key : field.key + 'Value';
      }
      return field.key;
    },
    fieldLabelClass(field) {
      return {
        error: this.errors.has(field.key)
      };
    },
    fieldSelectClass(field) {
      return {
        error: this.errors.has(field.key)
      };
    },
    sendUpdate(field) {
      if (Array.isArray(this.form[field])) {
        this.$emit(
          'updateField',
          field,
          this.coverageStepIndex,
          this.form[field][this.coverageStepIndex]
        );
      } else {
        this.$emit(
          'updateField',
          field,
          this.coverageStepIndex,
          this.form[field]
        );
      }
    },
    dateInputChanged(event, key, value) {
      const validator = this.$validator;
      validator.validate(key, value);
      this.$emit(
        'updateField',
        'policyEffectiveDate',
        this.coverageStepIndex,
        this.form.policyEffectiveDate
      );
      setTimeout(() => {
        if (
          this.errors.count() === 0 &&
          this.quoteResponse &&
          this.oldDate !== event
        ) {
          this.$emit('updateQuoteResponseToNull');
          this.oldDate = event;
        }
      }, 50);
    },
    changedPolicyDate(event) {
      if (this.quoteResponse) {
        this.$emit('updateQuoteResponseToNull');
      }
      this.form.policyEffectiveDate = moment(event).format('MM/DD/YYYY');
      this.selectedDate = null;
      this.oldDate = this.form.policyEffectiveDate;
      this.$el.querySelector('[name="policyEffectiveDate"]').focus();
      this.$validator.validate(
        'policyEffectiveDate',
        this.form.policyEffectiveDate
      );
      this.sendUpdate('policyEffectiveDate');
    },
    getTotalMonthlyFees() {
      return monthlyFees(this.selectedState, this.allVehiclesUnlimited);
    }
  },
  watch: {
    coverageFormWatch: {
      immediate: true,
      handler: function(newValue, oldValue) {
        if (newValue && oldValue && !_.isEqual(newValue, oldValue)) {
          this.form = _.cloneDeep(this.coverageForm);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.active {
  color: $primary;
}
.inactive {
  color: lightgrey;
}
.disabled {
  background-color: #b5b4ba;
}
.disabled__button__color {
  color: #ffffff !important;
}
button {
  border: none;
}
.box {
  padding: 1.5rem;

  @media (min-width: 1000px) {
    padding: 2rem;
  }

  @media (min-width: 1400px) {
    padding: 1rem 3rem;
    padding-bottom: 3.5rem;
  }
}
.total-cost-container {
  @media (min-width: 1000px) {
    margin-top: 1.5rem;
  }
}

.heading,
.estimated-cost {
  font-family: $family-sans-serif-bold;
  text-transform: none;
  letter-spacing: 0;
  font-size: inherit;
}

.heading {
  margin-bottom: 1rem;

  &:last-of-type {
    border-top: 1px solid $mischka;
    margin-top: 2rem;
    padding-top: 2rem;
  }
}

.right-section-first-container {
  border-top: none !important;
  margin-top: 0 !important;
}

.note {
  color: $mischka;
}

.input__field {
  min-height: 3.7rem;
}

.input__field:disabled {
  color: #737278;
}

.effective-date-field {
  margin-top: 1.75rem;
  @media (max-width: 480px) {
    margin-top: 1rem;
  }
}

@media (max-width: 760px) {
  .quote-wizard .button-primary + .button-primary.third-button {
    margin-top: 1rem;
    margin-left: 0;
  }
}
.tool-tip {
  color: #737278;
  top: 2px;
  left: 2px;
  .content {
    @media (min-width: 992px) {
      width: 420px;
    }
    @media (max-width: 991px) {
      width: 415px;
      left: 8px;
      i {
        left: 50%;
      }
    }
    @media (min-width: 992px) {
      left: 50%;
    }
    @media (max-width: 760px) {
      width: 300px;
      left: -15px;
      i {
        left: 57%;
      }
    }
  }
}
.multi-vehicle-chevron {
  display: flex;

  &__right {
    position: absolute;
    top: 200px;
    right: 12px;

    @media (min-width: 1000px) {
      right: 15px;
    }

    @media (min-width: 1400px) {
    }
  }
  &__left {
    position: absolute;
    top: 200px;
    left: 12px;

    @media (min-width: 1000px) {
      left: 15px;
    }

    @media (min-width: 1400px) {
    }
  }
}
.button-container {
  .button-left-align {
    display: flex;
    margin-top: 2rem;
    justify-content: flex-start;
  }

  .button-right-align {
    display: flex;
    margin-top: 2rem;
    justify-content: flex-end;
  }
}
</style>
